import { omitBy, union, reduce, mapValues } from 'lodash';
import {
  fetchDashboardItems,
  syncDashboardItem,
  upsertDashboardItem,
  deleteDashboardItems,
  duplicateDashboardItems,
  embedDashboardItem,
  moveDashboardItem,
} from 'action-creators/dashboard-items';
import { saveWhitelistedFields } from 'action-creators/dialog';

import actionTypes from 'constants/action-types';

const initialState = {
  entities: {}, // The cache of entity data keyed by _id.
  ids: [], // Drives the items to be rendered.
  errorMessage: '',
  isFetching: false,
};

const dashboardItems = (state = initialState, { type, payload }) => {
  switch (type) {
    case fetchDashboardItems.TRIGGER:
    case upsertDashboardItem.TRIGGER:
    case deleteDashboardItems.TRIGGER:
    case moveDashboardItem.TRIGGER:
      return {
        ...state,
        errorMessage: '',
      };

    case fetchDashboardItems.REQUEST:
      return {
        ...state,
        isFetching: true,
      };

    // This action should only be fetching full contents of a dashboard
    // So depending on what we're given, we need to remove all items relating to that dashboard
    // and add the new entities / ids (so we can handle remote deletion)
    case fetchDashboardItems.SUCCESS:
      // This should produce an array of size 1, but since `fetchDashboardItems` hasn't been officially
      // refactored away from the open-ended query style, for safety we still need to do it this way.
      // TODO: Investigate using Normalizr schema to solve entity relationship (dashboardItem -> dashboard)
      // TODO: Refactor the `fetchDashboardItems` action to only take in a dashboardId instead of a query
      const dashboardsFetched = reduce(
        payload.entities.dashboardItems,
        (acc, entity) => ({ ...acc, [entity.dashboardId]: true }),
        {}
      );

      // We want to not accidentally drop unrelated dashboard items when doing the fetch
      // so we need to get all the entities and ids of any dashboard items UNRELATED to what we're fetching
      const entitiesToKeep = reduce(
        state.entities,
        (acc, value, key) => {
          return dashboardsFetched[value.dashboardId]
            ? acc
            : {
                ...acc,
                entities: {
                  ...acc.entities,
                  [key]: value,
                },
                ids: [...acc.ids, value._id],
              };
        },
        {
          entities: {},
          ids: [],
        }
      );

      return {
        ...state,
        entities: {
          ...entitiesToKeep.entities,
          ...payload.entities.dashboardItems,
        },
        ids: union(entitiesToKeep.ids, payload.result),
      };

    case syncDashboardItem.SUCCESS:
    case upsertDashboardItem.SUCCESS:
    case duplicateDashboardItems.SUCCESS: {
      let itemsToUse = payload.entities.dashboardItems;
      if (type === upsertDashboardItem.SUCCESS) {
        // keep the existing embedding settings if any
        // otherwise embedding would appear to be disabled on UI
        // because embedding settings would be lost as they are not touched
        // and are thus not part of payload
        itemsToUse = mapValues(itemsToUse, (item, id) => {
          const staledItem = state.entities[id];
          return staledItem
            ? { ...item, embedding: staledItem.embedding }
            : item;
        });
      }
      return {
        ...state,
        entities: {
          ...state.entities,
          ...itemsToUse,
        },
        ids: union(state.ids, payload.result),
      };
    }

    case actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.GEOSPATIAL.SET_MAP_LEGEND: {
      const { _id, min, max } = payload;
      const { entities } = state;

      const updatedHeatmapLegend = entities[_id]
        ? {
            ...entities,
            [_id]: {
              ...entities[_id],
              meta: {
                ...entities[_id].meta,
                legends: { min, max },
              },
            },
          }
        : entities;

      return { ...state, entities: updatedHeatmapLegend };
    }

    case saveWhitelistedFields.SUCCESS:
    case embedDashboardItem.SUCCESS: {
      const { entities } = state;
      const { dashboardItemId, embedding, lastModifiedAt } = payload;
      return {
        ...state,
        entities: {
          ...entities,
          [dashboardItemId]: {
            ...entities[dashboardItemId],
            lastModifiedAt,
            embedding,
          },
        },
      };
    }

    case deleteDashboardItems.SUCCESS:
      if (payload._id) {
        return {
          ...state,
          entities: omitBy(state.entities, e => e._id === payload._id),
          ids: state.ids.filter(id => id !== payload._id),
        };
      }
      // Note: now that the deleteDashboardItems action creator accepts a
      // query selector, we can no longer remove the deleted items from the
      // reducer, as we don't necessarily know their _ids (e.g. when deleting
      // by providing the dashboardId). Therefore we don't modify the state
      // on deleteDashboardItems.SUCCESS. This is not a huge problem as the
      // entities can no longer be reached in the application, and any hard
      // reload will repopulate the cache correctly.
      return state;

    case fetchDashboardItems.FAILURE:
    case upsertDashboardItem.FAILURE:
    case deleteDashboardItems.FAILURE:
    case moveDashboardItem.FAILURE:
    case syncDashboardItem.FAILURE:
      return {
        ...state,
        errorMessage: payload.message,
      };

    case fetchDashboardItems.FULFILL:
      return {
        ...state,
        isFetching: false,
      };

    default:
      return state;
  }
};

export default dashboardItems;
export { dashboardItems };
